import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Per-execution diagnostic artifacts panel.
 *
 * Surfaces the artifact types captured per scenario by device-farm-hooks:
 *   - logcat       — Android logcat capture (tail on pass, full on fail).
 *                    Renders inline (collapsible, monospace) + download.
 *   - device_state — JSON snapshot (dumpsys meminfo, window state, getprop,
 *                    pidof). Failure-only. Renders pretty-printed inline +
 *                    download.
 *   - tombstone    — /data/anr/traces.txt + /data/tombstones/ dump.
 *                    Failure-only. Often empty / permission-denied on
 *                    production Android builds, so we don't auto-inline
 *                    unless the fetched body is non-empty.
 *
 * Each artifact also exposes:
 *   - "Copy URL"  → puts the signed GCS URL on the clipboard so it can be
 *                   pasted into an agent prompt / chat. URLs are GCS v4
 *                   signed and valid for ~60 min without auth.
 *   - "Download"  → straight <a href download> against the signed URL.
 *   - logcat:    "Copy tail" — fetches the URL and copies the last 200
 *                lines to clipboard for one-shot "show this to an agent".
 */

export interface DiagnosticArtifact {
  id: string;
  type: string;
  gcsPath: string;
  signedUrl?: string;
  fileName?: string | null;
  fileSizeBytes?: number | null;
}

interface DiagnosticsPanelProps {
  artifacts: DiagnosticArtifact[];
}

type DiagType = "logcat" | "device_state" | "tombstone";

const DIAG_TYPES: ReadonlyArray<{ type: DiagType; label: string; inlineByDefault: boolean }> = [
  { type: "logcat", label: "Logcat", inlineByDefault: false },
  { type: "device_state", label: "Device state", inlineByDefault: true },
  { type: "tombstone", label: "Tombstones", inlineByDefault: false }
];

const LOGCAT_TAIL_LINES = 200;

export function DiagnosticsPanel({ artifacts }: DiagnosticsPanelProps) {
  // Group artifacts by diagnostic type — there should normally be exactly one
  // of each, but a scenario could have retries that appended duplicates.
  const grouped = useMemo(() => {
    const map = new Map<DiagType, DiagnosticArtifact[]>();
    for (const a of artifacts) {
      if (a.type !== "logcat" && a.type !== "device_state" && a.type !== "tombstone") continue;
      const t = a.type as DiagType;
      const arr = map.get(t) ?? [];
      arr.push(a);
      map.set(t, arr);
    }
    return map;
  }, [artifacts]);

  const hasAny = grouped.size > 0;
  if (!hasAny) return null;

  return (
    <div style={{ marginTop: "var(--space-md)" }}>
      <div
        style={{
          fontSize: "var(--font-size-xs)",
          fontWeight: 600,
          color: "var(--color-text-secondary)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: "var(--space-xs)"
        }}
      >
        Diagnostics
      </div>
      {DIAG_TYPES.map(({ type, label, inlineByDefault }) => {
        const items = grouped.get(type);
        if (!items || items.length === 0) return null;
        return items.map((a, i) => (
          <DiagnosticItem
            key={a.id}
            label={items.length > 1 ? `${label} (${i + 1})` : label}
            artifact={a}
            inlineByDefault={inlineByDefault}
          />
        ));
      })}
    </div>
  );
}

interface DiagnosticItemProps {
  label: string;
  artifact: DiagnosticArtifact;
  /** Whether to expand the inline preview on first render. */
  inlineByDefault: boolean;
}

function DiagnosticItem({ label, artifact, inlineByDefault }: DiagnosticItemProps) {
  const [expanded, setExpanded] = useState(inlineByDefault);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"" | "url" | "tail">("");

  const href = artifact.signedUrl || artifact.gcsPath || "";
  const isFetchable = href.startsWith("http://") || href.startsWith("https://") || href.startsWith("/");

  const fetchBody = useCallback(async () => {
    if (!isFetchable) {
      setFetchError("Artifact URL is not fetchable (server did not generate a signed URL).");
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(href);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      setContent(text);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [href, isFetchable]);

  // Fetch on expand (or on mount when inlineByDefault).
  useEffect(() => {
    if (expanded && content == null && !loading && !fetchError && isFetchable) {
      fetchBody();
    }
    // We intentionally only react to `expanded` flips here — refetching on
    // every render would hammer GCS.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const copyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopyState("url");
      window.setTimeout(() => setCopyState(""), 1500);
    } catch {
      // clipboard blocked — fall back to a noop. Users still have the
      // "Download" link.
    }
  }, [href]);

  const copyTail = useCallback(async () => {
    let body = content;
    if (body == null) {
      if (!isFetchable) return;
      try {
        const res = await fetch(href);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        body = await res.text();
        setContent(body);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : String(err));
        return;
      }
    }
    const lines = body.split("\n");
    const tail = lines.slice(-LOGCAT_TAIL_LINES).join("\n");
    try {
      await navigator.clipboard.writeText(tail);
      setCopyState("tail");
      window.setTimeout(() => setCopyState(""), 1500);
    } catch {
      // ignore
    }
  }, [content, href, isFetchable]);

  const isLogcat = artifact.type === "logcat";
  const isDeviceState = artifact.type === "device_state";
  const isTombstone = artifact.type === "tombstone";

  // Tombstones are usually empty on production Android builds — don't
  // auto-render content even if expanded; require the user to actively
  // click "Show content" when there's something worth showing.
  const showInlineContent =
    expanded &&
    !loading &&
    !fetchError &&
    content != null &&
    (isTombstone ? content.trim().length > 0 : true);

  const prettyContent = useMemo(() => {
    if (content == null) return null;
    if (!isDeviceState) return content;
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      return content;
    }
  }, [content, isDeviceState]);

  const filename = artifact.fileName ?? defaultFilenameFor(artifact);
  const size = formatBytes(artifact.fileSizeBytes ?? null);

  return (
    <div
      style={{
        marginBottom: "var(--space-sm)",
        border: "1px solid var(--color-divider)",
        borderRadius: "var(--radius-sm)",
        overflow: "hidden",
        background: "var(--color-surface)"
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-xs)",
          padding: "var(--space-xs) var(--space-sm)",
          background: "var(--color-foreground)",
          fontSize: "var(--font-size-xs)",
          flexWrap: "wrap"
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            fontSize: "var(--font-size-xs)",
            color: "var(--color-text-primary)",
            cursor: "pointer",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4
          }}
        >
          <span style={{ display: "inline-block", width: 10 }}>{expanded ? "▾" : "▸"}</span>
          {label}
        </button>
        {size && (
          <span style={{ color: "var(--color-text-secondary)" }}>{size}</span>
        )}
        <div style={{ flex: 1 }} />
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          download={filename ?? undefined}
          style={{
            ...linkBtnStyle,
            color: "var(--color-info)"
          }}
          title={`Download ${label}`}
        >
          Download
        </a>
        <button
          type="button"
          onClick={copyUrl}
          disabled={!href}
          style={linkBtnStyle}
          title="Copy signed URL to clipboard"
        >
          {copyState === "url" ? "Copied!" : "Copy URL"}
        </button>
        {isLogcat && (
          <button
            type="button"
            onClick={copyTail}
            disabled={!isFetchable}
            style={linkBtnStyle}
            title={`Copy last ${LOGCAT_TAIL_LINES} lines for pasting into an agent prompt`}
          >
            {copyState === "tail" ? "Copied!" : `Copy tail (${LOGCAT_TAIL_LINES})`}
          </button>
        )}
      </div>

      {/* Body */}
      {expanded && (
        <div>
          {loading && (
            <div style={inlineMetaStyle}>Loading…</div>
          )}
          {fetchError && (
            <div style={{ ...inlineMetaStyle, color: "var(--color-error)" }}>
              Failed to load: {fetchError}
            </div>
          )}
          {expanded && !loading && !fetchError && content != null && isTombstone && content.trim().length === 0 && (
            <div style={inlineMetaStyle}>
              Empty / no tombstones captured (typical for production Android builds).
            </div>
          )}
          {showInlineContent && (
            <pre
              style={{
                margin: 0,
                padding: "var(--space-sm) var(--space-md)",
                background: "#0e141b",
                color: "#d1d9e1",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                lineHeight: 1.45,
                maxHeight: 320,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}
            >
              {prettyContent}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

const linkBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--color-divider)",
  borderRadius: "var(--radius-sm)",
  padding: "2px 8px",
  fontSize: "var(--font-size-xs)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  textDecoration: "none",
  fontFamily: "var(--font-family)"
};

const inlineMetaStyle: React.CSSProperties = {
  padding: "var(--space-sm) var(--space-md)",
  fontSize: "var(--font-size-xs)",
  color: "var(--color-text-secondary)",
  fontFamily: "var(--font-mono)"
};

function defaultFilenameFor(a: DiagnosticArtifact): string | null {
  const tail = a.gcsPath?.split("/").pop();
  if (tail && tail.length > 0 && !tail.includes("?")) return tail;
  switch (a.type) {
    case "logcat":
      return "logcat.txt";
    case "device_state":
      return "device-state.json";
    case "tombstone":
      return "tombstones.txt";
    default:
      return null;
  }
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
